import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Briefcase,
  MapPin,
  AlertCircle,
  Clock,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';
import PauseJobSessionModal from '@/components/modals/PauseJobSessionModal';

interface Job {
  id: number;
  booking_id: number;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  service: string;
  service_category?: string;
  address: string;
  postcode: string;
  booking_date: string;
  booking_time_start: string;
  estimated_duration: string;
  total_amount: string;
  notes: string;
  customer_notes: string;
  status: 'pending' | 'assigned' | 'accepted' | 'in_progress' | 'completed' | 'cancelled' | 'rejected';
  created_at: string;
  sla_minutes: number;
  accepted_at?: string;
  started_at?: string;
  completed_at?: string;
}

const ProviderActiveJobsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobFilter, setJobFilter] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [showPauseJobModal, setShowPauseJobModal] = useState(false);
  const [selectedJobForPause, setSelectedJobForPause] = useState<Job | null>(null);
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchJobs();
    }
  }, [user?.id, authLoading]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/assigned-jobs`);
      let jobsToDisplay: Job[] = [];
      if (response?.data && Array.isArray(response.data)) {
        jobsToDisplay = response.data;
      }
      const activeJobs = jobsToDisplay.filter((j) =>
        ['assigned', 'accepted', 'in_progress', 'confirmed'].includes(j.status)
      );
      setJobs(activeJobs);
    } catch {
      toast({ title: 'Error', description: 'Failed to load active jobs', variant: 'destructive' });
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (job: Job) => {
    if (job.status === 'in_progress') return 66;
    if (job.status === 'accepted' || job.status === 'assigned') return 33;
    return 0;
  };

  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'assigned':
        return <Badge className="bg-orange-100 text-orange-800">Assigned</Badge>;
      case 'accepted':
        return <Badge className="bg-blue-100 text-blue-800">Accepted</Badge>;
      case 'in_progress':
        return <Badge className="bg-purple-100 text-purple-800">In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'plumbing': 'bg-blue-100 text-blue-700',
      'electrical': 'bg-yellow-100 text-yellow-700',
      'hvac': 'bg-cyan-100 text-cyan-700',
      'general': 'bg-gray-100 text-gray-700',
      'heating': 'bg-red-100 text-red-700',
      'maintenance': 'bg-purple-100 text-purple-700',
    };
    return colors[category?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Authenticating...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  const acceptedJobs = jobs.filter(j => j.status === 'accepted');
  const inProgressJobs = jobs.filter(j => j.status === 'in_progress');
  const filteredJobs = jobFilter === 'all' 
    ? jobs 
    : jobFilter === 'accepted'
    ? acceptedJobs
    : inProgressJobs;

  return (
    <ProviderLayout>
      <Helmet>
        <title>Active Jobs - VireWire Provider</title>
        <meta name="description" content="Your active and accepted jobs" />
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Active Jobs</h1>
          <p className="text-gray-600 mt-1">Jobs you've accepted and are currently working on</p>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={jobFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setJobFilter('all')}
            className={jobFilter === 'all' ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            All ({jobs.length})
          </Button>
          <Button
            variant={jobFilter === 'accepted' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setJobFilter('accepted')}
            className={jobFilter === 'accepted' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            Accepted ({acceptedJobs.length})
          </Button>
          <Button
            variant={jobFilter === 'in_progress' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setJobFilter('in_progress')}
            className={jobFilter === 'in_progress' ? 'bg-purple-600 hover:bg-purple-700' : ''}
          >
            In Progress ({inProgressJobs.length})
          </Button>
        </div>

        {/* Jobs Grid */}
        {loading ? (
          <Card>
            <CardContent className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </CardContent>
          </Card>
        ) : filteredJobs.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">
                {jobFilter === 'all' 
                  ? 'No active jobs at the moment'
                  : `No ${jobFilter} jobs found`
                }
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate('/provider/available-jobs')}
              >
                Browse Available Jobs
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredJobs.map((job) => (
              <Card 
                key={job.id} 
                className="border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/provider/job/${job.id}`)}
              >
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {/* Top Row: Category & Status */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={getCategoryColor(job.service_category)}>
                          {job.service_category || 'General'}
                        </Badge>
                      </div>
                      {getStatusBadge(job.status)}
                    </div>

                    {/* Service Name */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{job.service}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                        <MapPin className="w-4 h-4" />
                        <span>{job.address}</span>
                        <span>•</span>
                        <span>{job.postcode}</span>
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Customer</p>
                      <p className="font-medium text-gray-900">{job.customer_name}</p>
                      <p className="text-sm text-gray-600 mt-1">{job.customer_phone}</p>
                    </div>

                    {/* Job Details Grid */}
                    <div className="grid grid-cols-2 gap-4 py-4 border-t border-b text-sm">
                      <div>
                        <p className="text-gray-600 text-xs uppercase font-semibold mb-1">📅 Scheduled</p>
                        <p className="font-medium">{new Date(job.booking_date).toLocaleDateString('en-GB')}</p>
                        <p className="text-xs text-gray-500">{job.booking_time_start}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-xs uppercase font-semibold mb-1">⏱️ Duration</p>
                        <p className="font-medium">{job.estimated_duration || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="bg-green-50 rounded-lg p-4 border-l-4 border-l-green-600">
                      <p className="text-xs text-gray-600 font-semibold uppercase mb-1">Quote Amount</p>
                      <p className="text-2xl font-bold text-gray-900">£{Number(job.total_amount).toFixed(2)}</p>
                    </div>

                    {/* Progress */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-700">Job Progress</span>
                        <span className="text-xs font-bold text-gray-700">
                          {job.status === 'accepted' ? '33%' : job.status === 'in_progress' ? '66%' : '0%'}
                        </span>
                      </div>
                      <Progress value={calculateProgress(job)} className="h-2" />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/provider/job/${job.id}`);
                        }}
                      >
                        View Job Details
                      </Button>
                      {job.status === 'in_progress' && (
                        <Button 
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedJobForPause(job);
                            setShowPauseJobModal(true);
                          }}
                        >
                          ⏸ Pause
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Accepted
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">{acceptedJobs.length}</p>
              <p className="text-xs text-gray-600 mt-1">Ready to start</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                In Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-purple-600">{inProgressJobs.length}</p>
              <p className="text-xs text-gray-600 mt-1">Currently working</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Total Earnings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">
                ${jobs.reduce((sum, job) => sum + Number(job.total_amount), 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-600 mt-1">From active jobs</p>
            </CardContent>
          </Card>
        </div>

        {/* Pause Job Session Modal */}
        {selectedJobForPause && (
          <PauseJobSessionModal
            jobId={`JOB-${selectedJobForPause.id}`}
            providerName={user?.name || 'Provider'}
            providerRole="Field Professional"
            jobValue={`$${Number(selectedJobForPause.total_amount).toFixed(2)} GBP`}
            isOpen={showPauseJobModal}
            onClose={() => {
              setShowPauseJobModal(false);
              setSelectedJobForPause(null);
            }}
            onConfirm={async (reason, providerNotes, customerNotes, pausePayout) => {
              try {
                const response: any = await apiService.request(
                  `/provider/jobs/${selectedJobForPause.id}/pause`,
                  'POST',
                  { reason, providerNotes, customerNotes, pausePayout }
                );
                if (response?.success) {
                  toast({ title: 'Job paused', description: response.message || 'Job session paused' });
                  setShowPauseJobModal(false);
                  setSelectedJobForPause(null);
                  fetchJobs();
                } else {
                  toast({
                    title: 'Failed to pause',
                    description: response?.message || 'Server rejected the pause request',
                    variant: 'destructive',
                  });
                }
              } catch (e: any) {
                toast({
                  title: 'Failed to pause',
                  description: e?.message || 'Network error',
                  variant: 'destructive',
                });
              }
            }}
          />
        )}
      </div>
    </ProviderLayout>
  );
};

export default ProviderActiveJobsPage;
